"use client";

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Tooltip, type TooltipSide } from "./Tooltip";

// Stage 5: local replacement for @datasirpi/ui's <IconButton>. Layout and
// CSS ported letter-for-letter from the vendor's compiled bundle and its
// IconButton CSS — new .rc-iconbutton class names. `aria-label` stays a
// required prop (via ButtonHTMLAttributes's own typing) exactly as the
// vendor declared it, since every real call site already provides one.
//
// Phase 4.4 adds `tooltip`. An icon-only button is the one control whose
// meaning is invisible, and 78 of them in this app leaned on the native
// `title` attribute to explain themselves. Native `title` is the worst
// available option for exactly this job: it waits about a second before
// appearing, cannot be styled, is routinely unreadable at high zoom, never
// appears on keyboard focus at all, and is ignored or mangled by several
// screen readers. Making the DS Tooltip reachable through a prop — rather
// than asking 57 files to wrap their buttons by hand — is what turns
// migrating off `title` into a mechanical change instead of a refactor.
export type IconButtonSize = "sm" | "md" | "lg";

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  "aria-label": string;
  size?: IconButtonSize;
  icon: ReactNode;
  /* Visible label on hover and on keyboard focus. Defaults to `aria-label`,
     so every existing call site gains a real tooltip with no edit — which is
     the point. Pass `false` for a button whose meaning is already carried by
     an adjacent visible label. */
  tooltip?: ReactNode | false;
  tooltipSide?: TooltipSide;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { size = "md", icon, className, type = "button", tooltip, tooltipSide = "top", title, ...rest },
  ref
) {
  const button = (
    <button
      ref={ref}
      type={type}
      className={cn("rc-iconbutton", `rc-iconbutton--${size}`, className)}
      /* `title` is deliberately dropped whenever a tooltip is rendered:
         keeping both means the native bubble surfaces a second later on top
         of the styled one, which reads as a rendering bug. */
      {...(tooltip === false && title ? { title } : {})}
      {...rest}
    >
      {icon}
    </button>
  );

  if (tooltip === false) return button;

  /* Resolution order is what makes the `title=` migration free.
     An explicit `tooltip` wins. Failing that, an existing native `title` is
     promoted into the DS tooltip — 78 icon buttons in this app already
     carry one, and several say more than their aria-label does ("Verify
     integrity" vs "Verify"), so throwing it away would lose real
     information. Only then does it fall back to the accessible name, which
     covers the remaining ~78 that had no visible explanation at all.
     Net effect: every icon button in the product gains a styled,
     keyboard-reachable tooltip without a single call site changing. */
  return (
    <Tooltip content={tooltip ?? title ?? rest["aria-label"]} side={tooltipSide}>
      {button}
    </Tooltip>
  );
});
